import { Component, OnInit, Input, ViewEncapsulation, OnDestroy, signal } from '@angular/core';
import { BaseButtonComponent } from '../base-button';
import { Checkout } from 'src/app/state/user/checkouts/models/checkout';
import { Subscription } from 'rxjs';
import { PhysicalRecord, HoldStatus } from 'src/app/models/physical-record';
import { HistoryItem } from '../../../models/history-user';
import { Hold } from 'src/app/state/user/holds/models/hold';

@Component({
  selector: 'app-record-buttons',
  templateUrl: './record-buttons.component.html',
  styleUrls: ['./record-buttons.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RecordButtonsComponent extends BaseButtonComponent implements OnInit, OnDestroy {
  checkout: Checkout;

  @Input()
  recordId: string;

  @Input()
  physicalRecord: PhysicalRecord;

  @Input()
  callback: () => void;

  @Input()
  historyItem: HistoryItem;

  @Input()
  certificateSection: boolean;

  @Input()
  isInfoPage: boolean;

  isValidLocation = true;
  isValidIp: boolean;
  isUserScreen: boolean;
  renewCheckouts: boolean;
  showVisualizeImageButton: boolean;
  visualizeImageButtonCallback: () => void;

  location$: Subscription;

  readonly isPhysicalItem = signal<boolean>(false);

  ngOnInit() {
    this.isUserScreen = this.getCurrentScreen() === 'USER_SCREENS';
    this.isValidIp = this.alreadyCheckedOut() || this.configService.get().validIp;
    this.location$ = this.broadcastService.userLocation$.subscribe(
      (value) => (this.isValidLocation = !this.configService.get().geolocation || this.alreadyCheckedOut() || value)
    );

    if (!this.record && this.recordId) {
      this.apiService.getRecord(this.recordId).subscribe((record) => (this.record = record));
    }

    this.showVisualizeImageButton = !this.authService.isAuthenticated();
    this.renewCheckouts = this.config && this.config.renew && this.configService.get().renewCheckouts;
    this.visualizeImageButtonCallback = () => {
      this.callback();
      this.showVisualizeImageButton = false;
    };
    this.checkout = this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId);

    if (this.record.resourceTypes.includes('TIPO_FISICO') && this.record.resourceTypes.length === 1) { 
      this.isPhysicalItem.set(true);
    }
  }

  ngOnDestroy() {
    this.location$.unsubscribe();
  }

  hasCheckoutFormats(): boolean {
    if (!this.record) {
      return false;
    }

    const formats = this.record.formats;

    if (!formats) {
      return false;
    }

    return (
      !this.record.externalLink &&
      formats.some((f) => f !== 'PDF' && f !== 'EPUB') &&
      this.record.resourceTypes.length > 0
    );
  }

  showPreviewButton(): boolean {
    if (!this.record || !this.record.active) {
      return false;
    }

    if (!this.config.preview || !this.record.hasPreview) {
      return false;
    }

    return !this.alreadyCheckedOut() && !this.record.externalLink && !this.record.resourceTypes.includes('TIPO_SCORM');
  }

  showFavoriteButton(): boolean {
    return this.config.favorite && this.authService.isAuthenticated();
  }

  showAddToListButton(): boolean {
    return this.config.list && this.authService.isAuthenticated() && !this.alreadyInList();
  }

  showAttachedResourceButton(): boolean {
    return (
      this.record &&
      this.record.active &&
      this.config.attachedDownload &&
      this.record &&
      this.record.attachedResource &&
      this.record.attachedResource.fileName !== undefined
    );
  }

  showCheckoutButton(): boolean {
    if (this.availability.availableToCheckout == null) {
      return this.availability.availableCopies > 0;
    } else {
      return this.availability.availableToCheckout;
    }
  }

  showHoldButton(): boolean {
    if (this.availability.availableToCheckout == null) {
      return this.availability.availableCopies === 0;
    } else {
      return !this.availability.availableToCheckout;
    }
  }

  showFollowButton(): boolean {
    return this.config.follow && !this.isUserListHolder();
  }

  getHold(): Hold {
    if (this.record.container) {
      return this.holdsFacade.getHoldByRecordId(this.record.id, this.issueDate, this.rssItem.rhId);
    } else {
      return this.holdsFacade.getHoldByRecordId(this.record.id, this.issueDate, this.rhId);
    }
  }

  alreadyCheckedOut(): boolean {
    if (!this.record) {
      return false;
    }

    if (this.record.containerData != null && this.isParentContainer()) {
      return false;
    }

    return this.checkoutsFacade.getCheckoutByRecordId(this.record.id, this.issueDate, this.rhId) != null;
  }

  alreadyInHold(): boolean {
    if (!this.record) {
      return false;
    }
    const hold = this.getHold();

    if (!hold) {
      return false;
    }

    const realHold = ['downloaded', 'cancelled', 'expired'];
    return !realHold.includes(hold?.status);
  }

  alreadyInList(): boolean {
    return this.record && this.userService.getListByRecordId(this.record.id) !== undefined;
  }

  onRenewal(renewal: Checkout) {
    this.renewed.emit(renewal);
  }

  disableRecord(active: boolean) {
    this.record.active = active;
  }

  get holdStatus(): typeof HoldStatus {
    return HoldStatus;
  }

  private isUserListHolder() {
    return (
      this.authService.isAuthenticated() &&
      this.record &&
      this.record.wishListInfo &&
      this.record.wishListInfo.associatedUser &&
      this.record.wishListInfo.associatedUser === `${Number(this.userService.getUserId())}`
    );
  }
}
